import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { apiToday, getApiEndpoints, longitud, initial, pathIframe } from "../../constantes/constantes.js";
import { getLegalTextBySponsor, fetchData } from "../../functions/functions.js";
import fetchDataInParallel from "../../services/getHomeResults.js";
import WidgetContainer from "../reusablesComponents/containerWidget.js";
import ArrowTabs from "./components/reusablesComponent/arrowsTabs.js";
import TabContent from "./components/reusablesComponent/tabContent.js";
import DateFilter from "./components/dateFilter/dateFilter.js";
import LiveFilter from "./components/liveFilter/liveFilter.js";
import SportFilter from "./components/sportFilter/sportFilter.js";
import LeagueFilter from "./components/leagueFilter/leagueFilter.js";
import Legal from "../legal/legal.js";
import Loading from "../loading/loading.js";
import './homeResults.sass';
import './components/cardF1Component/cardFormula1.sass'


export default function HomeResults({ mostrarMomios, patrocinador, mostrarBotonApostar, version }) {
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(initial);
  const [tabsData, setTabsData] = useState(Array(longitud).fill(null));
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [dataCache, setDataCache] = useState(new Map()); // Cache para datos de tabs
  const [initialLoadComplete, setInitialLoadComplete] = useState(false); // Flag para carga inicial
  const [selectedDate, setSelectedDate] = useState(new Date()); // Fecha seleccionada en el calendario
  const [isLiveOnly, setIsLiveOnly] = useState(false); // Filtro de eventos (false = mostrar NO en vivo, true = mostrar solo en vivo)
  const [selectedSport, setSelectedSport] = useState(null); // Deporte seleccionado
  const [selectedLeague, setSelectedLeague] = useState(null); // Liga seleccionada
  const [hasLiveGames, setHasLiveGames] = useState(false); // Si hay juegos en vivo en el primer render


  // Determinar si la fecha seleccionada es HOY
  const isViewingToday = useMemo(() => {
    const today = new Date();
    const todayDay = String(today.getDate()).padStart(2, '0');
    const todayMonth = String(today.getMonth() + 1).padStart(2, '0');
    const todayYear = String(today.getFullYear());

    const selectedDay = String(selectedDate.getDate()).padStart(2, '0');
    const selectedMonth = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const selectedYear = String(selectedDate.getFullYear());

    return todayDay === selectedDay && todayMonth === selectedMonth && todayYear === selectedYear;
  }, [selectedDate]);

  const legalText = getLegalTextBySponsor(patrocinador);

  const containerTabsRef = useRef(null);
  const arrowTabsRef = useRef(null);
  const hasLoadedRef = useRef(false); // Flag para evitar carga duplicada
  const cleanupRefreshRef = useRef(null); // Referencia para limpiar el refresh
  
  // Inicializar currentTabRef con la fecha de hoy
  const today = new Date();
  const todayFormatted = 
    String(today.getDate()).padStart(2, '0') + '/' +
    String(today.getMonth() + 1).padStart(2, '0') + '/' +
    String(today.getFullYear());
  const currentTabRef = useRef({ current: initial, date: todayFormatted }); // Referencia para currentTab y su fecha
  
  const selectedDateRef = useRef(new Date()); // Referencia para selectedDate

  // Actualizar la referencia cuando cambia currentTab
  useEffect(() => {
    currentTabRef.current.current = currentTab;
    // Obtener la fecha de la tab actual desde tabsData
    const currentTabData = tabsData[currentTab - 1];
    if (currentTabData?.date) {
      currentTabRef.current.date = currentTabData.date;
    }
  }, [currentTab, tabsData]);

  // Actualizar la referencia cuando cambia selectedDate
  useEffect(() => {
    // Convertir selectedDate a formato DD/MM/YYYY
    const dd = String(selectedDate.getDate()).padStart(2, '0');
    const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const yyyy = String(selectedDate.getFullYear());
    selectedDateRef.current = `${dd}/${mm}/${yyyy}`;
  }, [selectedDate]);

  // Cargar datos de HOY y calcular fechas de todas las tabs
  useEffect(() => {
    // Evitar ejecución duplicada
    if (hasLoadedRef.current) {
      return;
    }

    const loadInitialData = async () => {
      hasLoadedRef.current = true; // Marcar como cargado inmediatamente
      setLoading(true);

      try {
        const dynamicEndpoints = getApiEndpoints();
        
        // Usar la fecha actual del cliente para calcular las fechas de las tabs
        const today = new Date();
        const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

        const tabsStructure = [];

        for (let i = 0; i < longitud; i++) {
          const daysFromToday = i - (initial - 1);
          const targetDate = new Date(today);
          targetDate.setDate(today.getDate() + daysFromToday);

          const day = i === initial - 1 ? 'Hoy' : dayNames[targetDate.getDay()];
          const dd = String(targetDate.getDate()).padStart(2, '0');
          const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
          const yyyy = targetDate.getFullYear();
          const date = `${dd}/${mm}/${yyyy}`;

          tabsStructure[i] = {
            date: date,
            day: day,
            endpoint: dynamicEndpoints[i]?.primary || dynamicEndpoints[i],
            data: null
          };
        }

        setTabsData(tabsStructure);

        // Iniciar el servicio de refresh automático para HOY
        const cleanup = await fetchDataInParallel({
          fetchData,
          apiEndpoints: dynamicEndpoints,
          inicial: initial,
          longitud,
          apiToday,
          sportValue: null,
          currentTabRef,
          selectedDateRef,
          version,
          onDataLoaded: (data) => {
            // Actualizar datos de HOY
            updateTabData(initial - 1, data);

            // Detectar si hay juegos en vivo
            let hasLive = false;
            if (data?.sports) {
              for (const sport of data.sports) {
                if (sport.leagues && Array.isArray(sport.leagues)) {
                  for (const league of sport.leagues) {
                    if (league.games && Array.isArray(league.games)) {
                      if (league.games.some(game => game.class === 'clarosports-in-progress-game')) {
                        hasLive = true;
                        break;
                      }
                    }
                  }
                  if (hasLive) break;
                }
              }
            }
            setHasLiveGames(hasLive);

            // Si no hay juegos en vivo, activar el primer deporte disponible
            if (!hasLive && data?.sports?.length > 0) {
              setSelectedSport(data.sports[0].sport_code);
            }

            setInitialLoadComplete(true);
          },
          onUpdateData: (data, index) => {
            // Actualizar datos siempre
            updateTabData(index, data);
          },
          onError: (error) => {
            console.error("Error en el servicio de refresh:", error);
          },
          assignValuesToTab: (data, tabIndex) => {
            updateTabData(tabIndex - 1, data);
          }
        });

        cleanupRefreshRef.current = cleanup;

        // Scroll inicial
        if (isInitialLoad) {
          setTimeout(() => {
            if (containerTabsRef.current) {
              const widthTab = containerTabsRef.current.offsetWidth / 3;
              const scrollX = ((initial - 1) * widthTab) - (2 * widthTab);
              setScrollPosition(scrollX);
            }
          }, 100);
          setIsInitialLoad(false);
        }
      } catch (error) {
        console.error("Error cargando datos iniciales:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();

    // Cleanup al desmontar
    return () => {
      if (cleanupRefreshRef.current) {
        cleanupRefreshRef.current();
      }
    };
  }, []);

  // Resetear deporte cuando se activa el filtro (mostrar NO en vivo)
  useEffect(() => {
    if (isLiveOnly && selectedSport) {
      setSelectedSport(null);
    }
  }, [isLiveOnly]);

  // Activar primer deporte disponible cuando se desactiva el filtro (mostrar todos)
  useEffect(() => {
    if (!isLiveOnly && !selectedSport && tabsData[currentTab - 1]?.data?.sports?.length > 0) {
      const firstSport = tabsData[currentTab - 1].data.sports[0].sport_code;
      setSelectedSport(firstSport);
    }
  }, [isLiveOnly, currentTab, tabsData, selectedSport]);

  // Desactivar filtro cuando se selecciona un deporte (volver a mostrar todos)
  useEffect(() => {
    if (selectedSport && isLiveOnly) {
      setIsLiveOnly(false);
    }
  }, [selectedSport]);

  // Resetear liga seleccionada cuando cambia el deporte
  useEffect(() => {
    setSelectedLeague(null);
  }, [selectedSport]);

  // Función para actualizar datos de una tab específica
  const updateTabData = useCallback((tabIndex, data) => {
    //console.log("updateTabData called - tabIndex:", tabIndex, "data sports count:", data?.sports?.length);
    
    // Actualizar el cache
    setDataCache(prev => new Map(prev.set(tabIndex, data)));
    
    // Actualizar tabsData con un nuevo objeto para forzar re-render
    setTabsData(prev => {
      const newData = [...prev];
      if (newData[tabIndex]) {
        // Crear un nuevo objeto completamente para forzar la actualización
        newData[tabIndex] = { 
          ...newData[tabIndex], 
          data: data
        };
      }
      return newData;
    });
  }, []);

  // Efecto para actualizar la UI cuando vuelves a la tab HOY
  useEffect(() => {
    if (currentTab === initial && dataCache.has(initial - 1)) {
      const cachedData = dataCache.get(initial - 1);
      updateTabData(initial - 1, cachedData);
    }
  }, [currentTab]);

  // Crear callbacks para cada tab usando useMemo
  const onDataLoadedCallbacks = useMemo(() => {
    const callbacks = {};
    for (let i = 0; i < longitud; i++) {
      callbacks[i] = (data) => updateTabData(i, data);
    }
    return callbacks;
  }, [updateTabData, longitud]);

  // Manejar cambio de fecha desde el calendario
  const handleDateChange = async (newDate) => {
    setSelectedDate(newDate);
    setCurrentTab(initial); // Volver a la tab central
    setSelectedLeague(null); // Resetear liga seleccionada (mantener deporte)

    // Recalcular fechas de todas las tabs basadas en la nueva fecha
    const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const dynamicEndpoints = getApiEndpoints(newDate);
    const tabsStructure = [];

    // Obtener la fecha de hoy para comparación
    const today = new Date();
    const todayDay = String(today.getDate()).padStart(2, '0');
    const todayMonth = String(today.getMonth() + 1).padStart(2, '0');
    const todayYear = String(today.getFullYear());

    for (let i = 0; i < longitud; i++) {
      const daysFromCenter = i - (initial - 1);
      const targetDate = new Date(newDate);
      targetDate.setDate(newDate.getDate() + daysFromCenter);

      const dd = String(targetDate.getDate()).padStart(2, '0');
      const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
      const yyyy = targetDate.getFullYear();

      // Verificar si esta fecha es realmente hoy
      const isToday = dd === todayDay && mm === todayMonth && yyyy === todayYear;
      const day = isToday ? 'Hoy' : dayNames[targetDate.getDay()];
      const date = `${dd}/${mm}/${yyyy}`;

      // Limpiar todos los datos cuando cambias la fecha
      tabsStructure[i] = {
        date: date,
        day: day,
        endpoint: dynamicEndpoints[i]?.primary || dynamicEndpoints[i],
        data: null
      };
    }

    // Limpiar cache de todas las tabs cuando cambias la fecha
    setDataCache(new Map());

    // Cargar datos de la nueva fecha
    try {
      const todayEndpoint = dynamicEndpoints[initial - 1];
      let todayData;

      if (typeof todayEndpoint === 'string') {
        todayData = await fetchData(todayEndpoint);
      } else {
        todayData = await fetchData(todayEndpoint.primary);
      }

      // Actualizar la estructura con los datos cargados
      tabsStructure[initial - 1] = {
        ...tabsStructure[initial - 1],
        data: todayData
      };
    } catch (error) {
      console.error("Error cargando datos de la nueva fecha:", error);
    }

    // Actualizar tabsData con la estructura completa
    setTabsData(tabsStructure);

    // Centrar las tabs en la tab central después de cambiar la fecha
    setTimeout(() => {
      if (arrowTabsRef.current?.centerTab) {
        arrowTabsRef.current.centerTab(initial);
      }
    }, 0);
  };


  return (
    <WidgetContainer loading={loading} heightLoading={300} className="container__hub" id="container__hub">
      <div data-amx-sport="">
        {loading && (<Loading type={"300px"} />)}

        {!loading && (
          <>
            {/* contenedor de filtros */}
            <div className="filters-container">
              {/* componente de filtro de eventos en vivo */}
              <div className="filters-container__live-ligue">
                <LiveFilter
                  tabsData={tabsData}
                  currentTab={currentTab}
                  isLiveOnly={isLiveOnly}
                  onLiveChange={setIsLiveOnly}
                  isViewingToday={isViewingToday}
                />

                {/* componente de filtro de liga */}
                <LeagueFilter
                  tabsData={tabsData}
                  currentTab={currentTab}
                  selectedSport={selectedSport}
                  selectedLeague={selectedLeague}
                  onLeagueChange={setSelectedLeague}
                  isViewingToday={isViewingToday}
                  selectedDate={selectedDate}
                />
              </div>

              <div className="filters-container__date">
                {/* componente de filtro de fecha */}
                <DateFilter
                  selectedDate={selectedDate}
                  onDateChange={handleDateChange}
                  minDate={new Date(new Date().setMonth(new Date().getMonth() - 12))}
                  maxDate={new Date(new Date().getFullYear() + 1, 11, 31)}
                  isDisabled={isLiveOnly}
                />
              </div>

            </div>

            {/* componente de TABS */}
            <ArrowTabs
              ref={arrowTabsRef}
              longitud={longitud}
              tabsData={tabsData}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              scrollPosition={scrollPosition}
              contenedorTabsRef={containerTabsRef}
            />

            {/* componente leyenda legal */}
            {mostrarMomios === "true" && legalText.trim().length > 0 && (
              <div style={{ 'justifyContent': 'flex-end', 'display': 'flex' }} className="content-legal">
                <Legal patrocinador={patrocinador} />
              </div>
            )}

            {/* componente de filtro por deporte */}
            <SportFilter
              tabsData={tabsData}
              currentTab={currentTab}
              selectedSport={selectedSport}
              onSportChange={setSelectedSport}
              hasLiveGames={hasLiveGames}
              isViewingToday={isViewingToday}
            />

            {/* contenido de cada TAB */}
            <div className="tabs-panels">
              {initialLoadComplete && Array.from({ length: longitud }).map((_, idx) => (
                <div className="contenedor-tabs" key={`tab-${idx + 1}`} id={`renderWidgets${idx + 1}`} style={{ display: currentTab === idx + 1 ? 'block' : 'none' }}>
                  <TabContent
                    data={tabsData[idx]?.data}
                    index={idx}
                    pathIframe={pathIframe}
                    mostrarBotonApostar={mostrarBotonApostar}
                    mostrarMomios={mostrarMomios}
                    patrocinador={patrocinador}
                    endpoint={tabsData[idx]?.endpoint}
                    isActive={currentTab === idx + 1}
                    onDataLoaded={onDataLoadedCallbacks[idx]}
                    isLiveOnly={isLiveOnly}
                    selectedSport={selectedSport}
                    selectedLeague={selectedLeague}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </WidgetContainer>
  );
}